/**
 *  @class
 *  @function Predictive Search
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = new Map();
      this.controller = null;
    }
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('.search-field');
      this.predictiveSearchResults = this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');
      this.modal = this.closest('modal-dialog');
      this.defaultPredictiveHTML = this.predictiveSearchResults.innerHTML;

      this.setupEventListeners();
    }
    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));

      if (!this.modal) {
        this.predictiveSearchResults.addEventListener('focusout', this.onFocusOut.bind(this));
      }

      this.debouncedOnChange = debounce(() => {
        this.onChange();
      }, 300);

      this.input.addEventListener('input', () => {
        if (!this.getQuery().length) {
          this.closeResults();
          return;
        }
        this.debouncedOnChange();
      });

      if (this.cc) {
        this.cc.addEventListener('click', this.close.bind(this));
      }

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.close();
          }
        }
      });
      this.input.addEventListener('focus', this.onInputFocus.bind(this));

      if (this.modal) {
        this.modalObserver = new MutationObserver(() => {
          if (this.modal.hasAttribute('open')) {
            if (!this.getQuery().length) {
              this.closeResults();
            }
            requestAnimationFrame(() => this.input.focus());
          } else {
            this.resetSearch();
          }
        });
        this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      }

      this.predictiveSearchResults.addEventListener('click', (e) => {
        if (e.target.closest('#search-results-submit')) {
          this.form.submit();
        }
      });
    }

    disconnectedCallback() {
      if (this.modalObserver) {
        this.modalObserver.disconnect();
      }
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.closeResults();
        return;
      }
      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
      this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    onFocus() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        return;
      }

      this.getSearchResults(searchTerm);
    }

    getSearchResults(searchTerm) {
      if (this.cachedResults.has(searchTerm)) {
        this.renderSearchResults(searchTerm, this.cachedResults.get(searchTerm));
        return;
      }

      if (this.controller) {
        this.controller.abort();
      }
      this.controller = new AbortController();

      this.predictiveSearchResults.classList.add('loading');

      fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article,query,collection&${encodeURIComponent('resources[limit]')}=10&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku&section_id=predictive-search`, {
        signal: this.controller.signal
      })
        .then((response) => {
          if (this.getQuery() !== searchTerm) {
            return null;
          }
          this.predictiveSearchResults.classList.remove('loading');
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          if (!text || this.getQuery() !== searchTerm) {
            return;
          }
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

          if (this.cachedResults.size >= 50) {
            const firstKey = this.cachedResults.keys().next().value;
            this.cachedResults.delete(firstKey);
          }
          this.cachedResults.set(searchTerm, resultsMarkup);

          this.renderSearchResults(searchTerm, resultsMarkup);
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            return;
          }
          this.predictiveSearchResults.classList.remove('loading');
        });
    }

    renderSearchResults(searchTerm, resultsMarkup) {
      if (this.getQuery() !== searchTerm) {
        return;
      }
      this.predictiveSearchResults.innerHTML = resultsMarkup;
    }

    resetResultsMarkup() {
      this.predictiveSearchResults.innerHTML = this.defaultPredictiveHTML;
    }

    onInputFocus() {
      if (this.getQuery().length) {
        this.open();
        this.getSearchResults(this.getQuery());
      } else {
        this.closeResults();
      }
    }

    onFocusOut(event) {
      if (this.contains(event.relatedTarget)) {
        return;
      }
      this.close();
    }

    closeResults() {
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
      this.predictiveSearchResults.classList.remove('loading');
      this.resetResultsMarkup();
      this.predictiveSearchResults.setAttribute('inert', '');
      document.body.classList.remove('open-search');
    }

    resetSearch() {
      this.input.value = '';
      this.closeResults();
    }

    close() {
      this.closeResults();
      if (this.modal?.hasAttribute('open')) {
        this.modal.hide();
      }
    }
    open() {
      if (!this.getQuery().length) {
        return;
      }
      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
    }
  }
  customElements.define('search-form', SearchForm);
}
